import {ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {SplitViewComponent, SplitViewDirective} from '@webmate-web-core/shared/components/split-view/split-view.component';
import {SimpleEntryListComponent} from '@webmate-web-core/shared/components/split-view/components/entry-lists/simple-entry-list.component';
import {GenericFilterComponent} from '@webmate-web-core/shared/components/split-view/components/filters/generic-filter.component';
import {
    AppSplitViewDetailsComponent,
    AppVariantCallback
} from '@webmate-web-core/shared/components/apps/split-view/app-split-view-details.component';
import {VirtualPackage} from '@webmate-web-core/shared/types/packages/package';
import {ProjectId, VirtualPackageId} from '@webmate-web-core/shared/types/ids';
import {ActivatedRoute} from '@angular/router';
import {PackageService} from '@webmate-web-core/shared/services/package.service';
import {EntryActionConfig, GroupingConfig} from '@webmate-web-core/shared/components/split-view/components/entry-lists/entry-list-util';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {AdvancedSortingConfig} from '@webmate-web-core/shared/components/split-view/interfaces/sorting-filtering.interface';
import moment from 'moment';
import * as _ from 'lodash';

@Component({
    selector: 'wm-app-list',
    templateUrl: 'app-list.component.html',
    styleUrls: ['app-list.component.less'],
    standalone: true,
    imports: [
        SplitViewComponent,
        SimpleEntryListComponent,
        GenericFilterComponent,
        SplitViewDirective,
        AppSplitViewDetailsComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppListComponent implements OnChanges {

    readonly splitViewEmptyState = 'No apps found';

    readonly searchFn = (p: VirtualPackage, s: string) => p.name.toLowerCase().includes(s.toLowerCase()) || p.id.toLowerCase().includes(s.toLowerCase()) || p.platform.getFullName().toLowerCase().includes(s.toLowerCase());

    readonly sortingConfig = {
        type: 'advanced',
        defaultSortingOption: 'name',
        sortingOptions: [{
            id: 'name',
            displayName: 'Name A-Z',
            sortingFunction: (a: VirtualPackage, b: VirtualPackage) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        }, {
            id: 'uploaded',
            displayName: 'Creation Date',
            sortingFunction: (a: VirtualPackage, b: VirtualPackage) => -moment(a.creationTime).diff(moment(b.creationTime))
        }]
    } as AdvancedSortingConfig<VirtualPackage>;

    shouldDisplayGroups = false;
    readonly groupingConfig = {
        entryToGroup: (p: VirtualPackage) => p.platform.platformType,
        displayGroup: (platformType: string) => {
            switch (platformType) {
                case 'ios': return 'iOS';
                case 'android': return 'Android';
                default: return platformType;
            }
        },
        groupSortingFunction: (a: string, b: string) => a.localeCompare(b),
    } as GroupingConfig<string /*platformType*/, VirtualPackage>;

    readonly deleteActionConfig: EntryActionConfig<VirtualPackage> = {
        icon: 'trash-alt',
        hoverOnly: true,
        callback: (r: VirtualPackage) => this.deletePackage(r.id)
    };

    @Input() packages: Array<VirtualPackage>;

    @Input() projectId: ProjectId;

    @Input() variantCallbacks: AppVariantCallback;

    @Input() interactive: boolean = false;

    constructor(private route: ActivatedRoute, private packageService: PackageService, private notificationService: WmNotificationService) {
    }


    ngOnChanges(changes: SimpleChanges) {
        if (changes['packages']?.currentValue) {
            this.shouldDisplayGroups = _.uniq(this.packages.map(p => p.platform.platformType)).length > 1;
        }
    }

    private deletePackage(packageId: VirtualPackageId) {
        this.notificationService.open('Delete app?', 'Delete', () => {
            this.packageService.deletePackage(packageId, this.projectId).subscribe();
        }, 'Do not delete', () => {
        });

    }

}
